import App from './app/app';
import {
  Analytics,
  AuthenticatedSection,
  AuthProvider,
} from '@readcloud/common';
import { AppDispatch, reportActions } from '@readcloud/state';
import { TooltipProvider } from '@readcloud/ui';
import { Toaster } from '@readcloud/ui/sonner';
import * as React from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';
import { environment } from './environments/environment';
import { store } from './redux/store';
import { WebAppVersion, ContentVersion } from '@readcloud/common/config.json';
import { LoadingIcon } from '@readcloud/component-library';
import { AppUpdater } from './app/AppUpdater';

// Initialize OIDC authority URL for Tauri offline verification
const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;
if (isTauri) {
  import('@readcloud/fetch-wrapper')
    .then((module) => {
      module.setOidcAuthorityUrl(environment.auth.authority);
    })
    .catch((error) => {
      console.error('Failed to initialize fetch-wrapper:', error);
    });
}

interface IAppRootProps {}

const AppRoot: React.FunctionComponent<IAppRootProps> = (props) => {
  // Support legacy LTI logins via rc5 query parameter
  // TODO: Replace with proper LTI 1.3 implementation
  const queryParams = new URLSearchParams(window.location.search);
  const rc5 = queryParams.get('rc5');
  const extraQueryParams = rc5 ? { rc5 } : undefined;

  return (
    <React.Suspense
      fallback={
        <div className="rc-h-full rc-w-full rc-flex rc-flex-col rc-items-center rc-justify-center rc-bg-gray-100">
          <LoadingIcon loading position="absolute" />
        </div>
      }
    >
      <Provider store={store}>
        <BrowserRouter>
          <QueryParamProvider adapter={ReactRouter5Adapter}>
            <AuthProvider
              client_id={environment.auth.client_id}
              authority={environment.auth.authority}
              redirect_uri={environment.auth.redirect_uri}
              post_logout_redirect_uri={
                environment.auth.post_logout_redirect_uri
              }
              response_type={environment.auth.response_type}
              scope={environment.auth.scope}
              silent_redirect_uri={environment.auth.silent_redirect_uri}
              automaticSilentRenew={environment.auth.automaticSilentRenew}
              extraQueryParams={extraQueryParams}
              signinCallback={() => {
                (store.dispatch as AppDispatch)(
                  reportActions.asyncActions.createUserActivity({
                    type: 'Login',
                    'App Name': 'Webapp',
                    'Webapp Version': WebAppVersion,
                    'Content Version': ContentVersion,
                  })
                );
              }}
            >
              <TooltipProvider>
                <Analytics apiKey={environment.posthog_id}>
                  <AuthenticatedSection>
                    <App />
                  </AuthenticatedSection>
                  <div
                    style={{ position: 'absolute' }}
                    className="readcloud-ui readcloud-ui-reset"
                  ></div>
                </Analytics>
              </TooltipProvider>
            </AuthProvider>
          </QueryParamProvider>
          <Toaster />
          <AppUpdater />
        </BrowserRouter>
      </Provider>
    </React.Suspense>
  );
};

export default AppRoot;
